import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import CommentSection from "@/components/CommentSection";
import { getStory, createComment, likeStory, createStory } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Heart, ArrowLeft, Clock, Edit2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import ReportButton from "@/components/ReportButton";   // <-- new import

const CATEGORY_COLORS = {
  HIV: "bg-red-100 text-red-700 border-red-200",
  HPV: "bg-blue-100 text-blue-700 border-blue-200",
  HSV: "bg-green-100 text-green-700 border-green-200",
};

export default function StoryDetail() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editCategory, setEditCategory] = useState("");

  useEffect(() => {
    loadStory();
  }, [storyId]);

  const loadStory = async () => {
    try {
      const data = await getStory(storyId);
      setStory(data);
      setLiked(data.liked_by_user);
      setLikesCount(data.likes);
      setComments(data.comments || []);
      setEditTitle(data.title || "");
      setEditContent(data.content || "");
      setEditCategory(data.category);
    } catch (err) {
      toast.error("Story not found");
      navigate("/stories");
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    try {
      const result = await likeStory(storyId);
      setLiked(result.liked);
      setLikesCount((prev) => prev + (result.liked ? 1 : -1));
    } catch (err) {
      toast.error("Failed to like");
    }
  };

  const handleAddComment = async (parentId, content) => {
    try {
      await createComment(storyId, content, parentId);
      toast.success("Comment added!");
      loadStory();
    } catch (err) {
      toast.error("Failed to add comment");
    }
  };

  const handleEditStory = async () => {
    try {
      await api.put(`/stories/${storyId}`, {
        title: editTitle,
        content: editContent,
        category: editCategory,
      });
      toast.success("Story updated");
      setEditing(false);
      loadStory();
    } catch (err) {
      toast.error("Failed to update story");
    }
  };

  const handleDeleteStory = async () => {
    if (!window.confirm("Delete this story permanently?")) return;
    try {
      await api.delete(`/stories/${storyId}`);
      toast.success("Story deleted");
      navigate("/stories");
    } catch (err) {
      toast.error("Failed to delete story");
    }
  };

  const handleEditComment = async (commentId, newContent) => {
    try {
      await api.put(`/stories/${storyId}/comments/${commentId}`, { content: newContent });
      toast.success("Comment updated");
      loadStory();
    } catch (err) {
      toast.error("Failed to edit comment");
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/stories/${storyId}/comments/${commentId}`);
      toast.success("Comment deleted");
      loadStory();
    } catch (err) {
      toast.error("Failed to delete comment");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="text-sm text-[#6B6B70]">Loading...</div>
        </div>
      </div>
    );
  }

  if (!story) return null;

  const isAuthor = user?.user_id === story.user_id;

  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={() => navigate("/stories")} className="flex items-center gap-1 text-sm text-[#6B6B70] hover:text-[#0F0F10] mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to stories
        </button>

        <div className="bg-white border border-[#E7E5E0] rounded-2xl p-6 mb-6">
          {/* Author + Report button */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full overflow-hidden border border-[#E7E5E0]">
              <img src={story.author_avatar} alt="" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="font-medium text-sm">{story.author_name}</div>
              <div className="flex items-center gap-1 text-xs text-[#6B6B70]">
                <Clock className="w-3 h-3" /> {timeAgo(story.created_at)}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <ReportButton
                type="story"
                reportedUserId={story.user_id}
                storyId={story.story_id}
              />
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${CATEGORY_COLORS[story.category]}`}>
                {story.category}
              </span>
            </div>
          </div>

          {/* Content / Edit mode */}
          {editing ? (
            <div className="space-y-3">
              <input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="neo-input text-lg font-semibold"
                placeholder="Title"
              />
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="neo-input h-32 resize-none"
                placeholder="Your story..."
              />
              <div className="flex gap-2">
                {Object.keys(CATEGORY_COLORS).map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setEditCategory(cat)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border ${editCategory === cat ? CATEGORY_COLORS[cat] + " border-2" : "border-[#E7E5E0]"}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={handleEditStory} className="neo-btn bg-[#7C3AED] border-[#7C3AED] text-sm">Save</button>
                <button onClick={() => setEditing(false)} className="neo-btn neo-btn-secondary text-sm">Cancel</button>
              </div>
            </div>
          ) : (
            <>
              {story.title && <h1 className="text-xl font-semibold mb-3">{story.title}</h1>}
              <p className="text-[#404040] leading-relaxed whitespace-pre-wrap">{story.content}</p>
            </>
          )}

          {/* Story actions */}
          <div className="flex items-center gap-4 mt-6 pt-4 border-t border-[#F5F3EE]">
            <button onClick={handleLike} className="flex items-center gap-1.5 text-sm text-[#6B6B70] hover:text-[#DC2626] transition-colors">
              <Heart className={`w-5 h-5 ${liked ? "fill-[#DC2626] text-[#DC2626]" : ""}`} />
              {likesCount}
            </button>

            {isAuthor && !editing && (
              <div className="ml-auto flex gap-2">
                <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-sm text-[#7C3AED] hover:text-[#6D28D9]">
                  <Edit2 className="w-4 h-4" /> Edit
                </button>
                <button onClick={handleDeleteStory} className="flex items-center gap-1 text-sm text-red-500 hover:text-red-600">
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Comments */}
        <div className="bg-white border border-[#E7E5E0] rounded-2xl p-6">
          <CommentSection
            comments={comments}
            storyId={storyId}
            onAddComment={handleAddComment}
            currentUserId={user?.user_id}
            onEditComment={handleEditComment}
            onDeleteComment={handleDeleteComment}
          />
        </div>
      </main>
    </div>
  );
}